import React from "react";
import { Link, useLocation } from "react-router-dom";
import { CONTACTS } from "@shared/config/seo-schemas";
import { TelegramIcon } from "@shared/ui/icons";
import { Mail } from "lucide-react";

export const Footer: React.FC = () => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  return (
    <footer className="border-t border-border bg-background">
      <div className="container py-12">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-5 md:gap-2 lg:gap-4">
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <span className="font-rublev text-xl font-bold text-foreground tracking-super-wide">ПОЛОТНО</span>
            </Link>
            <p className="text-sm text-muted-foreground max-w-xs">
              Полотно — расшифровка аудио и видео в текст с помощью ИИ.
            </p>
          </div>

          <div>
            <h3 className="mb-4 font-light text-foreground">Продукт</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={isHomePage ? "#features" : "/#features"}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Возможности
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">
                  Тарифы
                </Link>
              </li>
              <li>
                <Link to="/telegram" className="text-muted-foreground hover:text-foreground transition-colors">
                  Telegram бот
                </Link>
              </li>
              <li>
                <Link to="/history" className="text-muted-foreground hover:text-foreground transition-colors">
                  История
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-light text-foreground">Инструменты</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/formats"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Форматы файлов
                </Link>
              </li>
              <li>
                <Link
                  to="/languages"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Языки
                </Link>
              </li>
              <li>
                <Link
                  to="/platforms"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Платформы и источники
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">
                  О сервисе
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Блог
                </Link>
              </li>
              <li>
                <Link
                  to="/use-cases"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Задачи
                </Link>
              </li>
              <li>
                <Link
                  to="/exports"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Форматы результата
                </Link>
              </li>
              <li>
                <Link
                  to="/youtube-to-text-converter"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  YouTube в текст
                </Link>
              </li>
              <li>
                <Link
                  to="/meeting-minutes"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Протоколы встреч
                </Link>
              </li>
              <li>
                <Link
                  to="/transcription-online"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Транскрибация онлайн
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-light text-foreground">Компания</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/contacts"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Контакты
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Конфиденциальность
                </Link>
              </li>
              <li>
                <Link
                  to="/offer"
                  className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  Оферта
                </Link>
              </li>
            </ul>
            <div className="mt-6">
              <h3 className="mb-3 font-light text-foreground">Поддержка</h3>
              <a
                href="https://t.me/Polotno_support_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <TelegramIcon className="h-4 w-4" />
                <span className="text-sm">Telegram бот</span>
              </a>
              <a
                href={`mailto:${CONTACTS.email}`}
                className="mt-2 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Mail className="h-4 w-4" />
                <span className="text-sm">{CONTACTS.email}</span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 border-t border-border pt-8 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Полотно — расшифровка аудио и видео в текст.</p>
          {/* Развязка с чужим SDK: по запросу «polotno» в выдаче стоит редактор с polotno.com */}
          <p className="mt-2 text-xs">Не связаны с Polotno SDK (polotno.com) — это библиотека canvas-редактора.</p>
        </div>
      </div>
    </footer>
  );
};
